'use client'
import classNames from 'classnames'
import type { DebouncedFuncLeading } from 'lodash'
import throttle from 'lodash/throttle'
import Link from 'next/link'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import React from 'react'
import { AddressPrompt } from '../address-prompt/AddressPrompt'
import { UserButton } from '../user-button/UserButton'
import styles from './HeaderMobile.module.scss'
import { useLocalisation } from '@/tools/i18n/client'
import { useIntersectionDetection } from '@/tools/intersection-detection/use-intersection-detection'
import { useErrorHandling } from '@/tools/o11y/client'
import {
  BarsIcon,
  IconButtonSize,
  IconButtonVariants,
  LogoSize,
  LogoSurface,
  LogoVariant,
  MainLogo,
} from '@/tools/ui'
import type { HeaderColorType } from '@/tools/ui/components/header/types'
import { NavigationMenu } from '@/tools/ui/components/navigation-menu'
import type { NavigationMenuContentType } from '@/tools/ui/components/navigation-menu/NavigationMenu'
import { UserSearchInput } from '@/tools/ui/components/user-search-input/UserSearchInput'

interface Props {
  className?: string
  shouldShowAddress?: boolean
  color?: HeaderColorType
  canShowSearch?: boolean
  NavigationMenuContent?: NavigationMenuContentType
  shouldShowUserButton?: boolean
  children?: React.ReactNode
  forceAddressPicker?: boolean
}

export const HeaderMobile: FC<Props> = ({
  className,
  shouldShowAddress,
  canShowSearch,
  color,
  NavigationMenuContent,
  shouldShowUserButton,
  children,
  forceAddressPicker,
}) => {
  const { getLanguage } = useLocalisation()
  const { t } = useLocalisation()

  const language = getLanguage()
  const isBrandHeader = color === 'brand'
  const logoSurface = isBrandHeader ? LogoSurface.onBrand : LogoSurface.onPrimary
  const { execWithErrorTracking, withErrorTracking } = useErrorHandling()
  const [scrolled, setScrolled] = useState(false)
  const { isIntersecting, isAbove } = useIntersectionDetection(
    'hero-container-input',
    'header-mobile'
  )
  const isIntersectingOrAbove = isIntersecting || isAbove

  useEffect(() => {
    let handleScroll: DebouncedFuncLeading<() => void> | undefined

    execWithErrorTracking(() => {
      handleScroll = throttle(() => {
        const offset = window.scrollY
        setScrolled(offset > 0)
      }, 200)

      window.addEventListener('scroll', handleScroll)
    })

    return withErrorTracking(() => {
      if (handleScroll) {
        handleScroll.cancel()
        window.removeEventListener('scroll', handleScroll)
      }
    })
  }, [execWithErrorTracking, withErrorTracking])

  return (
    <div
      className={classNames({
        [styles.intersection]: isIntersectingOrAbove,
        [styles.headerMobile]: true,
        [className ?? '']: true,
        [styles.scrolled]: scrolled && !isBrandHeader,
      })}
      id={'header-mobile'}
    >
      <section className={styles.topContainer}>
        <div className={styles.leftSection}>
          {NavigationMenuContent && (
            <NavigationMenu
              NavigationMenuContent={NavigationMenuContent}
              aria-label={t('store-wall.categories-menu')}
              icon={BarsIcon}
              size={IconButtonSize.small}
              variant={IconButtonVariants.neutral}
            />
          )}
          <Link className={styles.logoLink} href={`/${language}`} prefetch={false}>
            <MainLogo
              className={styles.logoImage}
              size={LogoSize.medium}
              surface={logoSurface}
              variant={LogoVariant.logotype}
            />
          </Link>
        </div>
        {shouldShowUserButton && (
          <UserButton color={color} isIntersecting={isIntersectingOrAbove} isMobile={true} />
        )}
      </section>
      {shouldShowAddress && (
        <AddressPrompt
          color={color}
          forceAddressPicker={forceAddressPicker}
          isIntersecting={isIntersectingOrAbove}
          showLocationScrollDrawer={false}
        />
      )}
      {children}
      {canShowSearch && (
        <div className={styles.searchInput}>
          <UserSearchInput />
        </div>
      )}
    </div>
  )
}
